
import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { useClientInfo } from "@/hooks/use-client-info";
import { useWindowActive } from "@/hooks/use-window-active";

export const useNotifications = () => {
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const { clientId, isAdmin } = useClientInfo();
  const isWindowActive = useWindowActive();
  const intervalRef = useRef<number | null>(null);
  const channelRef = useRef<any>(null);

  const fetchUnreadCount = useCallback(async () => {
    if (!isWindowActive) return;
    
    try {
      let query = supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('read', false);
      
      // Se è un cliente, filtra per client_id
      if (!isAdmin && clientId) {
        query = query.eq('client_id', clientId);
      }
      
      const { count, error } = await query;
      
      if (error) {
        throw error;
      }
      
      setUnreadCount(count || 0);
    } catch (error) {
      console.error('Errore nel conteggio delle notifiche non lette:', error);
    }
  }, [clientId, isAdmin, isWindowActive]);

  const markAllAsRead = useCallback(async () => {
    try {
      let query = supabase
        .from('notifications')
        .update({ read: true })
        .eq('read', false);
      
      // Se è un cliente, filtra per client_id
      if (!isAdmin && clientId) {
        query = query.eq('client_id', clientId);
      }
      
      const { error } = await query;
      
      if (error) {
        throw error;
      }
      
      await fetchUnreadCount();
      return true;
    } catch (error) {
      console.error('Errore nel segnare le notifiche come lette:', error);
      return false;
    }
  }, [clientId, isAdmin, fetchUnreadCount]);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      let query = supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);
      
      // Se è un cliente, filtra per client_id
      if (!isAdmin && clientId) {
        query = query.eq('client_id', clientId);
      }
      
      const { error } = await query;
      
      if (error) {
        throw error;
      }
      
      await fetchUnreadCount();
      return true;
    } catch (error) {
      console.error('Errore nel segnare la notifica come letta:', error);
      return false;
    }
  }, [clientId, isAdmin, fetchUnreadCount]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      let query = supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);
      
      // Se è un cliente, filtra per client_id
      if (!isAdmin && clientId) {
        query = query.eq('client_id', clientId);
      }
      
      const { error } = await query;
      
      if (error) {
        throw error;
      }
      
      await fetchUnreadCount();
      return true;
    } catch (error) {
      console.error('Errore nell\'eliminazione della notifica:', error);
      return false;
    }
  }, [clientId, isAdmin, fetchUnreadCount]);

  useEffect(() => {
    if ((isAdmin !== null && isAdmin) || (!isAdmin && clientId)) {
      // Esegui il fetch iniziale
      fetchUnreadCount();
      
      // Imposta interval solo quando la finestra è attiva
      if (isWindowActive) {
        intervalRef.current = window.setInterval(fetchUnreadCount, 30000); // Intervallo più lungo (30 secondi)
      } else if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      
      // Configurazione del canale per le notifiche in tempo reale
      let channelFilter = {};
      
      // Se è un cliente, filtra per client_id
      if (!isAdmin && clientId) {
        channelFilter = { filter: `client_id=eq.${clientId}` };
      }
      
      // Utilizza un unico canale per tutti gli eventi
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
      
      channelRef.current = supabase
        .channel('notification_updates')
        .on('postgres_changes', 
          { 
            event: '*', // Ascolta tutti gli eventi (INSERT, UPDATE, DELETE)
            schema: 'public', 
            table: 'notifications',
            ...channelFilter
          }, 
          () => {
            if (isWindowActive) {
              fetchUnreadCount();
            }
          }
        )
        .subscribe();
      
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        
        if (channelRef.current) {
          supabase.removeChannel(channelRef.current);
          channelRef.current = null;
        }
      };
    }
  }, [isAdmin, clientId, fetchUnreadCount, isWindowActive]);

  return {
    unreadCount,
    fetchUnreadCount,
    markAllAsRead,
    markAsRead,
    deleteNotification
  };
};
